import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { authApi } from '../services/authApi';
import { authStorage } from '../services/authStorage';
import { Certification, CertificationResponse } from '../types/auth';
import { ApiError } from '../services/authApi';
import CertificateTemplate, { CertificateData } from './CertificateTemplate';
import '../styles/Certifications.css';
import { formatDate } from '../utils/dateUtils';

interface CertificationsProps {
  isAdmin?: boolean;
  currentUser?: any;
  showTitle?: boolean;
  companyId?: number; // Optional company ID for filtering
}

const Certifications: React.FC<CertificationsProps> = ({
  isAdmin = false,
  currentUser,
  showTitle = true,
  companyId
}) => {
  const { t } = useTranslation();

  // State for real certification data
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [selectedCertificate, setSelectedCertificate] = useState<Certification | null>(null);

  // Load certifications from API
  const loadCertifications = async () => {
    try {
      setLoading(true);
      setError('');
      const token = authStorage.getAccessToken();
      const userData = authStorage.getUserData();

      if (!token) {
        setError('Authentication token not found. Please log in again.');
        return;
      }

      // For admin users, load all certifications or filtered by companyId if provided
      // For company users, load certifications for their company
      const targetCompanyId = isAdmin ? companyId : userData?.companyId;

      const response: CertificationResponse = await authApi.getCertifications(
        token,
        targetCompanyId || undefined
      );

      setCertifications(response.certifications || []);
    } catch (error) {
      console.error('Failed to load certifications:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to load certifications. Please try again.');
      }
      setCertifications([]);
    } finally {
      setLoading(false);
    }
  };

  // Load certifications on component mount and when dependencies change
  useEffect(() => {
    loadCertifications();
  }, [isAdmin, companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to convert API Certification to CertificateData for template
  const convertToCertificateData = (certification: Certification): CertificateData => {
    return {
      id: certification.id.toString(),
      name: `Certification for ${certification.company.companyName}`,
      recipient: certification.company.companyName,
      issuer: 'INFRASEC AB',
      issueDate: formatDate(certification.certificationDate),
      expiryDate: formatDate(new Date(new Date(certification.certificationDate).getFullYear() + 1, new Date(certification.certificationDate).getMonth(), new Date(certification.certificationDate).getDate()).toISOString()),
      description: '',
      companyName: certification.company.companyName,
      certificateNumber: `CERT-${certification.id}-${Math.random().toString(36).substr(2, 8).toUpperCase()}`,
      authorizedBy: 'Erik Lindberg - Chief Compliance Officer',
      status: 'valid' // All active certifications are valid
    };
  };

  // Helper function to determine certificate status based on date
  const getCertificateStatus = (certificationDate: string): 'valid' | 'expiring' | 'expired' => {
    const issueDate = new Date(certificationDate);
    const now = new Date();
    const expiryDate = new Date(issueDate.getFullYear() + 1, issueDate.getMonth(), issueDate.getDate());
    const daysUntilExpiry = Math.floor((expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) return 'expired';
    if (daysUntilExpiry <= 30) return 'expiring';
    return 'valid';
  };

  const handlePreview = (certification: Certification) => {
    setSelectedCertificate(certification);
  };

  const handleDownload = (certification: Certification) => {
    // Open certificate in new window and trigger print dialog to save as PDF
    const htmlContent = certification.certificationHTMLContent || convertToCertificateData(certification);
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Certificate - ${certification.company.companyName}</title>
            <style>
              @media print {
                @page { margin: 0; }
                body { margin: 0; }
              }
              body { font-family: Arial, sans-serif; }
            </style>
          </head>
          <body>
            ${typeof htmlContent === 'string' ? htmlContent : ''}
          </body>
        </html>
      `);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'valid': return '#10b981';
      case 'expiring': return '#f59e0b';
      case 'expired': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'valid': return t('certifications.valid', 'Valid');
      case 'expiring': return t('certifications.expiring', 'Expiring Soon');
      case 'expired': return t('certifications.expired', 'Expired');
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="certifications-container">
        {showTitle && <h2>{t('certifications.title', 'Certifications')}</h2>}
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>{t('common.loading', 'Loading certifications...')}</p>
        </div>
      </div>
    );
  }

  // Show error state with retry option
  if (error) {
    return (
      <div className="certifications-container">
        {showTitle && <h2>{t('certifications.title', 'Certifications')}</h2>}
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>{t('certifications.error', 'Error Loading Certifications')}</h3>
          <p>{error}</p>
          <button onClick={loadCertifications} className="retry-button">
            {t('common.retry', 'Try Again')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="certifications-container">
      {showTitle && <h2>{t('certifications.title', 'Certifications')}</h2>}

      {/* Certifications grid */}
      <div className="certifications-grid">
        {certifications.length > 0 ? (
          certifications.map(certification => {
            const status = getCertificateStatus(certification.certificationDate);
            return (
              <div key={certification.id} className="certificate-card">
                <div className="certificate-preview">
                  <div className={`certificate-type-icon ${status}`}>
                    <div className="certificate-icon">🏆</div>
                    <div className="certificate-type-text">CERT</div>
                  </div>
                </div>

                <div className="certificate-info">
                  <div className="certificate-name">TCS Certified</div>
                  <div className="certificate-meta">
                    <div className="certificate-issuer">INFRASEC AB</div>
                    <div className="certificate-date">{formatDate(certification.certificationDate)}</div>
                  </div>
                  <div className="certificate-details">
                    <div className="certificate-company">{certification.company.companyName}</div>
                  </div>
                  {isAdmin && (
                    <div className="certificate-org-number">
                      <span style={{ fontSize: '11px', color: '#666', fontStyle: 'italic' }}>
                        Org: {certification.company.orgNumber}
                      </span>
                    </div>
                  )}
                </div>

                <div className="certificate-actions">
                  <button onClick={() => handleDownload(certification)} className="download-btn">
                    ⬇ {t('certifications.download', 'Download')}
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="no-certificates">
            <div className="empty-state">
              <div className="empty-state-icon">🏆</div>
              <h3>{t('certifications.noCertificates', 'No Certifications Available')}</h3>
              <p>{t('certifications.noCertificatesDesc', 'No certifications found for this company.')}</p>
            </div>
          </div>
        )}
      </div>

      {/* Certificate modal */}
      {selectedCertificate && (
        <div className="certificate-modal-overlay" onClick={() => setSelectedCertificate(null)}>
          <div className="certificate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>TCS Certified</h2>
              <button
                className="close-button"
                onClick={() => setSelectedCertificate(null)}
                aria-label={t('common.close', 'Close') || 'Close'}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              {selectedCertificate.certificationHTMLContent ? (
                <div
                  className="certificate-html-content"
                  dangerouslySetInnerHTML={{ __html: selectedCertificate.certificationHTMLContent }}
                />
              ) : (
                <CertificateTemplate certificateData={convertToCertificateData(selectedCertificate)} />
              )}
            </div>
            <div className="modal-actions">
              <button
                onClick={() => handleDownload(selectedCertificate)}
                className="download-action-btn"
              >
                {t('certifications.downloadCertificate', 'Download Certificate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Certifications;
